import React, { useState } from 'react';
import { AlertTriangle, Clock, RefreshCw, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

/**
 * EmailDeliveryPanel
 * Shows system emails — welcome notes, deactivation notices — that did not get
 * through. Without it the retry queue is invisible: a welcome email can be stuck
 * or given up on and the only way to find out is to read the database.
 *
 * Renders nothing when there is nothing wrong, so it stays out of the way.
 */

const TYPE_LABELS = {
  welcome:              'Welcome email',
  deactivation:         'Deactivation notice',
  reactivation:         'Reactivation notice',
  email_changed:        'Email-changed notice',
  role_changed:         'Role-changed notice',
  admin_password_reset: 'Password-reset notice',
};

function formatWhen(value) {
  if (!value) return '';
  const d = new Date(value);
  const diffMin = Math.round((d - Date.now()) / 60000);
  if (diffMin > 0) {
    if (diffMin < 60) return `in ${diffMin} min`;
    return `in ${Math.round(diffMin / 60)} hr`;
  }
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function EmailDeliveryPanel({ summary, items, onRetry, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [notice, setNotice] = useState('');

  const pending = summary?.pending || 0;
  const failed  = summary?.failed || 0;

  // Nothing to report — say nothing
  if (pending === 0 && failed === 0) return null;

  async function handleRetry(id) {
    setRetryingId(id);
    setNotice('');
    try {
      const result = await onRetry(id);
      setNotice(result?.message || 'Retry attempted');
    } catch (err) {
      setNotice(err.message || 'Retry failed');
    } finally {
      setRetryingId(null);
      if (onRefresh) onRefresh();
    }
  }

  const tone = failed > 0
    ? { bg: 'bg-red-50',   border: 'border-red-200',   text: 'text-red-800',   sub: 'text-red-600',   icon: 'text-red-600' }
    : { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', sub: 'text-amber-600', icon: 'text-amber-600' };

  return (
    <div className={`${tone.bg} border ${tone.border} rounded-xl p-4 mb-6`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 ${failed > 0 ? 'bg-red-100' : 'bg-amber-100'} rounded-full flex items-center justify-center flex-shrink-0`}>
            {failed > 0
              ? <AlertTriangle className={`w-4 h-4 ${tone.icon}`} />
              : <Clock className={`w-4 h-4 ${tone.icon}`} />}
          </div>
          <div>
            <p className={`text-sm font-semibold ${tone.text}`}>
              {failed > 0 && `${failed} system email${failed !== 1 ? 's' : ''} could not be delivered`}
              {failed > 0 && pending > 0 && ' · '}
              {pending > 0 && `${pending} still retrying`}
            </p>
            <p className={`text-xs ${tone.sub} mt-0.5`}>
              {failed > 0
                ? `Gave up after ${summary?.maxAttempts || 7} attempts over ${summary?.retryWindowHours || 25} hours. These people were never notified.`
                : `Retrying automatically for up to ${summary?.retryWindowHours || 25} hours.`}
            </p>
          </div>
        </div>

        <button
          onClick={() => setExpanded(e => !e)}
          className={`flex items-center space-x-1 text-xs font-medium ${tone.text} hover:underline`}
        >
          <span>{expanded ? 'Hide' : 'View'} details</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {notice && (
        <div className="mt-3 flex items-center space-x-2 text-xs text-gray-700 bg-white/70 rounded-lg px-3 py-2">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {expanded && (
        <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Recipient</th>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Reason</th>
                <th className="text-left px-4 py-2 font-medium">Next try</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(items || []).map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-gray-900">{item.recipient_name || '—'}</p>
                    <p className="text-xs text-gray-500">{item.recipient_email}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {TYPE_LABELS[item.email_type] || item.label || item.email_type}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 max-w-xs">
                    <p className="text-xs text-gray-600 truncate" title={item.last_error || ''}>
                      {item.last_error || '—'}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {item.attempts} of {item.max_attempts} attempts
                    </p>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">
                    {item.status === 'failed'
                      ? <span className="text-red-600 font-medium">Gave up</span>
                      : formatWhen(item.next_attempt_at)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => handleRetry(item.id)}
                      disabled={retryingId === item.id}
                      className="inline-flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3 h-3 ${retryingId === item.id ? 'animate-spin' : ''}`} />
                      <span>{retryingId === item.id ? 'Trying...' : 'Retry now'}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-[11px] text-gray-400 px-4 py-2 bg-gray-50 border-t border-gray-100">
            A welcome email that never arrived means that person has an account but no way in.
            They stay counted as awaiting onboarding until one gets through.
          </p>
        </div>
      )}
    </div>
  );
}
